import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import usmApi from "@/lib/usmApi";
import { useUsmAuth } from "@/contexts/UsmAuthContext";
import { PageHeader } from "../UsmLayout";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, ArrowLeft } from "lucide-react";

const EXPECTED_HEADERS = ["barcode", "name", "category", "material", "weight_grams", "branch", "status", "sale_price_try", "purchase_price_try", "sku", "notes"];

const SAMPLE_CSV = `barcode,name,category,material,weight_grams,branch,status,sale_price_try,purchase_price_try
8690000000001,Trabzon Hasır Bilezik,gumus,925 gümüş,18.4,TR-MERKEZ,active,4500,3200
8690000000002,18 Ayar Tek Taş Yüzük,altin,18 ayar,3.2,TR-MERKEZ,active,12500,9800
`;

export default function InventoryImport() {
  const { hasPerm } = useUsmAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [skipDup, setSkipDup] = useState(true);
  const [branches, setBranches] = useState([]);
  const [defaultBranch, setDefaultBranch] = useState("");

  React.useEffect(() => {
    usmApi.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {});
  }, []);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      try {
        const { headers: hs, rows: rs } = parseCSV(text);
        setHeaders(hs);
        setRows(rs);
        setPreview(null);
        toast.success(`${rs.length} satır okundu`);
      } catch (err) {
        toast.error(err.message || "CSV okunamadı");
      }
    };
    reader.readAsText(file);
  };

  const buildPayload = () => ({
    rows: rows.map((r) => ({
      barcode: r.barcode || "",
      name: r.name || "",
      category: r.category || null,
      material: r.material || null,
      weight_grams: numOrNull(r.weight_grams),
      branch: r.branch || null,
      status: (r.status || "active").toLowerCase(),
      sale_price_try: numOrNull(r.sale_price_try),
      purchase_price_try: numOrNull(r.purchase_price_try),
      sku: r.sku || null,
      notes: r.notes || null,
    })),
    skip_duplicates: skipDup,
    default_branch_id: defaultBranch || null,
  });

  const validate = async () => {
    if (rows.length === 0) return;
    setValidating(true);
    try {
      const r = await usmApi.post("/inventory/import/validate", buildPayload());
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Doğrulama başarısız"); }
    finally { setValidating(false); }
  };

  const commit = async () => {
    if (!preview || preview.summary.valid === 0) return;
    if (!window.confirm(`${preview.summary.valid} ürün içe aktarılacak. Devam edilsin mi?`)) return;
    setCommitting(true);
    try {
      const r = await usmApi.post("/inventory/import/commit", buildPayload());
      toast.success(`${r.data.summary.inserted} ürün eklendi · ${r.data.summary.skipped_duplicates} atlandı`);
      setTimeout(() => navigate("/usm/inventory"), 800);
    } catch (e) { toast.error(e?.response?.data?.detail || "İçe aktarma başarısız"); }
    finally { setCommitting(false); }
  };

  if (!hasPerm("inventory.create")) {
    return <p className="text-sm text-[#71717A]">Bu sayfa için yetkiniz yok.</p>;
  }

  return (
    <div data-testid="usm-inventory-import">
      <PageHeader
        title="CSV İçe Aktarma"
        breadcrumbs={[{ label: "Panel", to: "/usm" }, { label: "Envanter", to: "/usm/inventory" }, { label: "İçe Aktarma" }]}
        actions={
          <button onClick={() => navigate("/usm/inventory")} className="text-xs uppercase tracking-[0.2em] px-4 py-3 hover:bg-[#FAFAF9] inline-flex items-center gap-2">
            <ArrowLeft size={14} /> Envantere Dön
          </button>
        }
      />

      <div className="bg-white border border-[#E7E5E4] p-6 mb-6">
        <div className="flex flex-wrap items-start gap-4">
          <label data-testid="csv-upload-label" className="flex flex-col items-center justify-center w-64 h-32 border-2 border-dashed border-[#E7E5E4] hover:border-[#C8A96A] cursor-pointer transition-colors">
            <Upload size={20} className="text-[#71717A] mb-2" />
            <span className="text-xs uppercase tracking-[0.18em] text-[#52525B]">CSV Dosyası Seç</span>
            <span className="text-[10px] text-[#A1A1AA] mt-1">UTF-8, virgül ayraçlı</span>
            <input data-testid="csv-file-input" type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} />
          </label>
          <div className="flex-1 min-w-[200px] text-xs text-[#52525B] space-y-1">
            <p className="font-semibold text-[#0A0A0A]">Beklenen sütunlar (sırasız):</p>
            <code className="block font-mono text-[10px] bg-[#FAFAF9] px-3 py-2">{EXPECTED_HEADERS.join(", ")}</code>
            <p>• <code>branch</code> şube ID, kodu (TR-MERKEZ) veya adı olabilir.</p>
            <p>• <code>category</code> kategori ID, slug veya Türkçe adı olabilir.</p>
            <p>• <code>status</code>: active, reserved, sold, repair, transfer, lost.</p>
            <a href={`data:text/csv;charset=utf-8,${encodeURIComponent(SAMPLE_CSV)}`} download="usm-inventory-template.csv" className="inline-flex items-center gap-1 text-[#C8A96A] hover:underline mt-1">
              <FileSpreadsheet size={11} /> Örnek şablonu indir
            </a>
          </div>
        </div>

        {rows.length > 0 && (
          <>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs items-end">
              <label className="flex items-center gap-2 text-[#52525B]">
                <input data-testid="skip-dup-toggle" type="checkbox" checked={skipDup} onChange={(e) => setSkipDup(e.target.checked)} />
                Var olan barkodları atla (yinelenenler oluşturulmaz)
              </label>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.15em] text-[#71717A] mb-1">Boş şube alanları için varsayılan şube</label>
                <select value={defaultBranch} onChange={(e) => setDefaultBranch(e.target.value)} className="w-full px-3 py-2 border border-[#E7E5E4] text-sm">
                  <option value="">— Yok —</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.code})</option>)}
                </select>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              <button data-testid="validate-btn" onClick={validate} disabled={validating} className="bg-[#0A0A0A] text-white text-xs uppercase tracking-[0.2em] px-6 py-3 disabled:opacity-50">
                {validating ? "Doğrulanıyor..." : `${rows.length} Satırı Doğrula`}
              </button>
              <button onClick={() => { setRows([]); setHeaders([]); setPreview(null); }} className="text-xs uppercase tracking-[0.2em] px-4 py-3 hover:bg-[#FAFAF9]">Temizle</button>
            </div>
          </>
        )}
      </div>

      {preview && <PreviewSection preview={preview} onCommit={commit} committing={committing} />}
    </div>
  );
}

function PreviewSection({ preview, onCommit, committing }) {
  const { summary, valid = [], errors = [], duplicates = [] } = preview;
  return (
    <div data-testid="import-preview" className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat testid="stat-total" label="Toplam satır" value={summary.total_rows} />
        <Stat testid="stat-valid" label="Geçerli" value={summary.valid} tone="text-emerald-700" />
        <Stat testid="stat-errors" label="Hatalı" value={summary.errors} tone="text-red-700" />
        <Stat testid="stat-dup" label="DB'de Mevcut" value={summary.duplicates_in_db} tone="text-amber-700" />
      </div>

      {errors.length > 0 && (
        <Section title="Hatalı Satırlar" icon={<AlertTriangle size={14} className="text-red-600" />} testid="errors-section">
          <RowTable rows={errors} showErrors />
        </Section>
      )}
      {duplicates.length > 0 && (
        <Section title="Mevcut Barkodlar (Atlanacak)" icon={<AlertTriangle size={14} className="text-amber-600" />} testid="duplicates-section">
          <RowTable rows={duplicates} />
        </Section>
      )}
      {valid.length > 0 && (
        <Section title="İçe Aktarılacaklar" icon={<CheckCircle2 size={14} className="text-emerald-600" />} testid="valid-section">
          <RowTable rows={valid} />
        </Section>
      )}

      <div className="flex justify-end gap-3">
        <button
          data-testid="commit-btn"
          onClick={onCommit}
          disabled={committing || summary.valid === 0}
          className="bg-[#C8A96A] hover:bg-[#A6864A] disabled:opacity-40 text-white text-xs uppercase tracking-[0.25em] px-8 py-4 inline-flex items-center gap-3"
        >
          <CheckCircle2 size={14} /> {committing ? "Aktarılıyor..." : `${summary.valid} Ürünü İçe Aktar`}
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "", testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#E7E5E4] px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">{label}</div>
      <div className={"font-heading text-2xl mt-1 " + tone}>{value}</div>
    </div>
  );
}

function Section({ title, icon, children, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#E7E5E4]">
      <div className="px-4 py-3 border-b border-[#E7E5E4] flex items-center gap-2">
        {icon}
        <span className="text-[11px] uppercase tracking-[0.2em] text-[#52525B]">{title}</span>
      </div>
      {children}
    </div>
  );
}

function RowTable({ rows, showErrors = false }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs min-w-[700px]">
        <thead className="bg-[#FAFAF9] text-[10px] uppercase tracking-[0.15em] text-[#71717A]">
          <tr>
            <th className="text-left px-3 py-2">#</th>
            <th className="text-left px-3 py-2">Barkod</th>
            <th className="text-left px-3 py-2">Ad</th>
            <th className="text-left px-3 py-2">Durum</th>
            <th className="text-right px-3 py-2">Satış</th>
            {showErrors && <th className="text-left px-3 py-2">Hata</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.row_number} className="border-t border-[#F5F5F4]">
              <td className="px-3 py-2 font-mono text-[10px] text-[#A1A1AA]">{r.row_number}</td>
              <td className="px-3 py-2 font-mono text-[11px]">{r.resolved?.barcode || r.input?.barcode || "—"}</td>
              <td className="px-3 py-2">{r.resolved?.name || r.input?.name || "—"}</td>
              <td className="px-3 py-2">{r.resolved?.status}</td>
              <td className="px-3 py-2 text-right font-finance">{r.resolved?.sale_price_try ?? "—"}</td>
              {showErrors && <td className="px-3 py-2 text-red-700">{(r.errors || []).join(" · ")}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ────────────────────────────  CSV parser  ──────────────────────────────
// Minimal RFC-4180-style parser. Handles quoted fields, escaped quotes,
// and embedded commas/newlines inside quotes. Sufficient for typical CSV
// exports from Excel / Google Sheets.
function parseCSV(text) {
  if (!text || !text.trim()) throw new Error("Boş dosya");
  const rows = [];
  let cur = [""];
  let inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"' && text[i + 1] === '"') { cur[cur.length - 1] += '"'; i++; }
      else if (ch === '"') { inQ = false; }
      else { cur[cur.length - 1] += ch; }
    } else {
      if (ch === '"') inQ = true;
      else if (ch === ",") cur.push("");
      else if (ch === "\n") { rows.push(cur); cur = [""]; }
      else if (ch === "\r") { /* skip */ }
      else cur[cur.length - 1] += ch;
    }
  }
  if (cur.length > 1 || cur[0] !== "") rows.push(cur);
  if (rows.length === 0) throw new Error("Veri yok");
  const rawHeaders = rows[0].map((h) => (h || "").trim().toLowerCase());
  const out = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    if (row.length === 1 && (row[0] || "").trim() === "") continue;
    const obj = {};
    rawHeaders.forEach((h, i) => { obj[h] = (row[i] ?? "").trim(); });
    out.push(obj);
  }
  return { headers: rawHeaders, rows: out };
}

function numOrNull(v) {
  if (v == null || v === "") return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}
